
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Studio {
  id: string;
  name: string;
  description?: string;
  address?: string;
  slug?: string;
  phone?: string;
  is_registered?: boolean;
  rating_avg: number;
  rating_count: number;
  geo_location?: {
    latitude: number;
    longitude: number;
  };
}

export const useStudios = (category: string | null = null, searchQuery: string = '') => {
  const [studios, setStudios] = useState<Studio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchStudios = async () => {
      setLoading(true);
      try {
        // Use explicit type assertion to avoid deep type instantiation error
        let query = (supabase
          .from('studios')
          .select('*') as any);
        
        // Add filters if provided
        if (category) {
          query = query.eq('category', category);
        }
        
        if (searchQuery) {
          query = query.or(`name.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%,address.ilike.%${searchQuery}%`);
        }
        
        const { data, error } = await query;

        if (error) {
          setError(error);
        } else {
          // Safely map the raw data to our Studio type
          const processedStudios: Studio[] = (data || []).map((item: any) => ({
            id: item.id || '',
            name: item.name || '',
            description: item.description || '',
            address: item.address || '',
            slug: item.slug || '',
            phone: item.phone || '',
            is_registered: item.is_registered || false,
            rating_avg: Number(item.rating_avg || 0),
            rating_count: Number(item.rating_count || 0),
            geo_location: item.geo_location || undefined
          }));
          setStudios(processedStudios);
        }
      } catch (err: any) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchStudios();
  }, [category, searchQuery]);

  return { studios, loading, error };
};

// Add the default export
export default useStudios;
